import { getTheme, ITheme } from '@fluentui/react';
import * as AdaptiveCards from 'adaptivecards';
import 'adaptivecards/dist/adaptivecards.css';
import AppContext from 'App/AppContext';
import * as React from 'react';
import './adaptiveCard.fluent.css';

const baseClassName = 'fluentAdaptiveCard';

type JsonValue = null | boolean | number | string | JsonObject | JsonValue[];
export type JsonObject = { [key: string]: JsonValue };

const isJsonObject = (v: unknown): v is JsonObject => typeof v === 'object' && v !== null && !Array.isArray(v);

const tryParseJsonObject = (value: unknown): JsonObject | null => {
  if (!value) return null;

  if (isJsonObject(value)) return value;

  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);

      return isJsonObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

// Inputs in adaptivecards submit come back as strings ("true"/"false"/text etc.)
type AdaptiveInputs = Record<string, string>;

export type AdaptiveSubmit<TActionData extends JsonObject = JsonObject> = {
  actionData: TActionData;
  inputs: AdaptiveInputs;
};

interface AdaptiveCardRendererProps<TActionData extends JsonObject = JsonObject> {
  /**
   * The adaptive card JSON.
   * Can be:
   * - a JSON string returned by the API
   * - a parsed object already
   */
  card: string | JsonObject;
  /**
   * Action.Submit payload.
   * You'll get:
   * - actionData: whatever you put in SubmitAction.Data (typed)
   * - inputs: all input values keyed by input id (strings)
   */
  onSubmit?: (payload: AdaptiveSubmit<TActionData>) => void;
  onInputChanged?: (inputs: AdaptiveInputs) => void;
  className?: string;
  disabled: boolean;
  /**
   * What the card is, for building it: the rendered card is kept while this stays the same. A host that writes the
   * typed values back into `card` (to show them again after a reload) passes the card without those values here, so a
   * keystroke never rebuilds the card and takes the input's focus. Defaults to the card itself.
   */
  cardKey?: string;
}

const buildHostConfig = (theme: ITheme, isDark: boolean) => {
  const bg = isDark ? theme.palette.themeDarker : theme.palette.themeLighterAlt;
  const fg = isDark ? theme.palette.neutralLight : theme.palette.neutralPrimary;
  const subtle = theme.palette.neutralSecondary;
  const separator = isDark ? theme.palette.neutralTertiaryAlt : theme.palette.neutralLight;

  return new AdaptiveCards.HostConfig({
    fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
    spacing: { small: 4, default: 8, medium: 12, large: 16, extraLarge: 20, padding: 12 },
    separator: { lineThickness: 1, lineColor: separator },
    supportsInteractivity: true,
    actions: {
      maxActions: 3,
      buttonSpacing: 8,
      actionsOrientation: 'horizontal',
      actionAlignment: 'left',
    },
    containerStyles: {
      default: {
        backgroundColor: bg,
        foregroundColors: {
          default: { default: fg, subtle },
          accent: { default: theme.palette.themePrimary, subtle: theme.palette.themePrimary },
          attention: { default: theme.palette.redDark, subtle: theme.palette.red },
          good: { default: theme.palette.greenDark, subtle: theme.palette.green },
          warning: { default: theme.palette.orangeLighter, subtle: theme.palette.orangeLight },
        },
      },
    },
  });
};

const extractInputs = (data: unknown): AdaptiveInputs => {
  // AdaptiveCards merges Action.Submit.Data + all inputs into one object
  // Inputs are strings in practice; keep only string values.
  if (!isJsonObject(data)) return {};

  const inputs: AdaptiveInputs = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === 'string') inputs[k] = v;
  }

  return inputs;
};

const extractActionData = <TActionData extends JsonObject>(data: unknown): TActionData | null => {
  // If you nest your custom data under a known key (recommended), you can make this stricter.
  // For now: accept the full object and let caller interpret by schema.
  if (!isJsonObject(data)) return null;

  return data as TActionData;
};

const getAllInputs = (card: AdaptiveCards.AdaptiveCard): AdaptiveInputs => {
  const inputValues: AdaptiveInputs = {};
  const inputs = card.getAllInputs();

  for (const input of inputs) {
    const inputId = input.id;
    if (!inputId) continue;

    const value = (input as unknown as { value?: unknown }).value;
    if (value === undefined || value === null) continue;

    inputValues[inputId] = String(value);
  }

  return inputValues;
};

// An OpenUrl's address as an absolute http(s) address, a relative one against the app's own origin; undefined for anything
// else, so a card cannot open a script or a data address
const toOpenableUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  try {
    const resolved = new URL(url, window.location.origin);

    return resolved.protocol === 'https:' || resolved.protocol === 'http:' ? resolved.href : undefined;
  } catch {
    return undefined;
  }
};

const disableCardElements = (card: AdaptiveCards.AdaptiveCard) => {
  // for (let i = 0; i < card.getItemCount(); i++) {
  //   const element: AdaptiveCards.CardElement = card.getItemAt(i);
  // }
  // card.getAllInputs().forEach(input => {

  //   });

  card.getAllActions().forEach((action) => {
    action.isEnabled = false;
  });
};

export const AdaptiveCardRenderer = <TActionData extends JsonObject = JsonObject>(
  props: AdaptiveCardRendererProps<TActionData>,
) => {
  const { card, onSubmit, onInputChanged, className } = props;

  const hostRef = React.useRef<HTMLDivElement>(null);
  const appContext = React.useContext(AppContext);
  const theme = getTheme();

  // The card is built once per card key: the latest card and callbacks are read through refs, because a host passes new
  // ones on every render, and a rebuild replaces the inputs' DOM (the focus and the caret go with it).
  const cardRef = React.useRef(card);
  cardRef.current = card;
  const onSubmitRef = React.useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onInputChangedRef = React.useRef(onInputChanged);
  onInputChangedRef.current = onInputChanged;
  const cardKey = props.cardKey ?? (typeof card === 'string' ? card : JSON.stringify(card));
  const reportsInputs = onInputChanged !== undefined;

  React.useEffect(() => {
    const payload = tryParseJsonObject(cardRef.current);
    const host = hostRef.current;
    if (!host) return;

    host.innerHTML = '';
    if (!payload) return;

    const adaptiveCard = new AdaptiveCards.AdaptiveCard();
    adaptiveCard.hostConfig = buildHostConfig(theme, appContext.useDarkMode);

    adaptiveCard.onExecuteAction = (action) => {
      // the library opens no address itself: an OpenUrl (a settings page, a saved file's library link) opens in a new tab.
      // A disabled card's actions never get here (disableCardElements)
      if (action instanceof AdaptiveCards.OpenUrlAction) {
        const url = toOpenableUrl(action.url);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');

        return;
      }
      if (!(action instanceof AdaptiveCards.SubmitAction)) return;
      const submit = onSubmitRef.current;
      if (!submit) return;

      const data = action.data as unknown;

      const actionData = extractActionData<TActionData>(data);
      if (!actionData) return;

      const inputs = extractInputs(data);

      submit({ actionData, inputs });
    };

    if (reportsInputs) {
      adaptiveCard.onInputValueChanged = () => {
        onInputChangedRef.current?.(getAllInputs(adaptiveCard));
      };
    }

    try {
      adaptiveCard.parse(payload);

      if (props.disabled) {
        disableCardElements(adaptiveCard);
      }

      const rendered = adaptiveCard.render();
      if (rendered) {
        host.appendChild(rendered);
      }
    } catch {
      // optional: log
    }

    return () => {
      host.innerHTML = '';
    };
  }, [cardKey, reportsInputs, appContext.useDarkMode, theme, props.disabled]);

  const modeClass = appContext.useDarkMode ? `${baseClassName}--dark` : `${baseClassName}--light`;

  //
  // Main render
  //
  const cssVars: React.CSSProperties & Record<string, string> = {
    '--acThemePrimary': theme.palette.themePrimary,
    '--acNeutralPrimary': theme.palette.neutralPrimary,
    '--acNeutralSecondary': theme.palette.neutralSecondary,
    '--acWhite': theme.palette.white,
    '--acCheckboxSize': '20px',
  };

  return <div style={cssVars} className={`${baseClassName} ${modeClass} ${className ?? ''}`.trim()} ref={hostRef} />;
};
